import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";

// bookings.journey_type: 1 one way, 2 round trip, 3 multi city
const TRIP_TYPE_LABELS = { 1: "One Way", 2: "Round Trip", 3: "Multi City" };

function useDebounce(value, delay = 500) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function JsonViewerModal({ title, raw, onClose }) {
  let parsed = null;
  let err = null;
  if (raw != null && raw !== "") {
    const str = typeof raw === "string" ? raw : JSON.stringify(raw);
    try {
      parsed = JSON.parse(str);
    } catch (e) {
      err = e.message;
    }
  }
  const formatted = parsed != null ? JSON.stringify(parsed, null, 2) : (raw ?? "(empty)");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold hover:bg-gray-100"
          >
            Close
          </button>
        </div>
        <div className="p-4 overflow-auto flex-1 min-h-0">
          {err ? (
            <p className="text-sm text-red-600">Invalid JSON: {err}</p>
          ) : (
            <pre className="text-sm text-gray-800 bg-gray-50 p-4 rounded-xl border overflow-x-auto whitespace-pre-wrap font-mono">
              {formatted}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BookingsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1, groupCount: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [bookingId, setBookingId] = useState("");
  const [userId, setUserId] = useState(searchParams.get("user_id") || "");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [payloadModal, setPayloadModal] = useState({ open: false, title: "", raw: null });

  const fetchBookings = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;

    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/bookings", {
        params: {
          search: debouncedSearch || undefined,
          booking_id: bookingId || undefined,
          user_id: userId || undefined,
          page: nextPage,
          limit: nextLimit,
          sortBy: "created_datetime",
          order: "DESC",
        },
      });

      setGroups(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1, groupCount: 0 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load bookings";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings({ page: 1 });
  }, []);

  useEffect(() => {
    setPage(1);
    fetchBookings({ page: 1 });
  }, [debouncedSearch]);

  useEffect(() => {
    setPage(1);
    fetchBookings({ page: 1 });
  }, [bookingId, userId]);

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchBookings({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchBookings({ page: p });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 20;
    setLimit(v);
    setPage(1);
    fetchBookings({ page: 1, limit: v });
  };

  const clearFilters = () => {
    setSearch("");
    setBookingId("");
    setUserId("");
    setPage(1);
    fetchBookings({ page: 1 });
  };

  const openEdit = (group) => {
    if (!group?.trace_id) return;
    navigate(`/admin/bookings/edit/trace/${group.trace_id}`);
  };

  const openPayload = (title, raw) => {
    setPayloadModal({ open: true, title, raw });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Bookings</h1>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            Clear Filters
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/bookings/create")}
            className="rounded-xl bg-black text-white px-4 py-2 text-sm font-semibold hover:bg-gray-800"
          >
            Create Booking
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Booking ID / Ticket # / PNR..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[260px]">
          <label className="text-sm font-semibold">Booking ID</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Filter by booking_id"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[260px]">
          <label className="text-sm font-semibold">User ID</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Filter by user_id"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Booking List </span>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>

            <button
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : groups.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No bookings found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Ticket Reference</th>
                <th className="px-4 py-3">Username</th>
                {/* <th className="px-4 py-3">Payment ID</th> */}
                <th className="px-4 py-3">PNR</th>
                <th className="px-4 py-3">Trip Type</th>
                <th className="px-4 py-3">Origin</th>
                <th className="px-4 py-3">Destination</th>
                <th className="px-4 py-3">Booked On</th>
                {/* <th className="px-4 py-3">Payloads</th> */}
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="border-t">
              {groups.map((group) => {
                const bookings = group.bookings || [];
                const primary =
                  bookings.find((b) => b.trip_indicator_id === 1) ||
                  bookings.find((b) => b.trip_indicator_id == null) ||
                  bookings[0];
                if (!primary) return null;
                const displayPaymentId =
                  primary.payment_id || (bookings.find((b) => b.payment_id)?.payment_id) || "-";
                return (
                  <tr key={group.trace_id || primary.booking_id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">{primary.booking_ticket_number || "-"}</td>
                    <td className="px-4 py-3">{primary.username || "-"}</td>
                    {/* <td className="px-4 py-3">{displayPaymentId}</td> */}
                    <td className="px-4 py-3">{primary.pnr || "-"}</td>
                    <td className="px-4 py-3">{TRIP_TYPE_LABELS[Number(primary.journey_type)] || "-"}</td>
                    <td className="px-4 py-3">{primary.origin || "-"}</td>
                    <td className="px-4 py-3">{primary.destination || "-"}</td>
                    <td className="px-4 py-3">{primary.created_at ? new Date(primary.created_at).toLocaleString() : "-"}</td>
                    {/* <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs font-medium hover:bg-gray-100 disabled:opacity-50"
                          disabled={!primary.search_payload}
                          onClick={() => openPayload("Search Payload", primary.search_payload)}
                        >
                          Search
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs font-medium hover:bg-gray-100 disabled:opacity-50"
                          disabled={!primary.actual_payload}
                          onClick={() => openPayload("Actual Payload", primary.actual_payload)}
                        >
                          Actual
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs font-medium hover:bg-gray-100 disabled:opacity-50"
                          disabled={!primary.fare_info}
                          onClick={() => openPayload("Fare Info", typeof primary.fare_info === "object" ? JSON.stringify(primary.fare_info) : primary.fare_info)}
                        >
                          Fare info
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs font-medium hover:bg-gray-100 disabled:opacity-50"
                          disabled={!primary.fare_detail}
                          onClick={() => openPayload("Fare Detail", primary.fare_detail)}
                        >
                          Fare detail
                        </button>
                      </div>
                    </td> */}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openEdit(group)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {payloadModal.open && (
        <JsonViewerModal
          title={payloadModal.title}
          raw={payloadModal.raw}
          onClose={() => setPayloadModal({ open: false, title: "", raw: null })}
        />
      )}
    </div>
  );
}
